import { useMutation, useQuery } from "@tanstack/react-query";
import { KeyRound, LogOut, ShieldAlert, Trash2, UserRound } from "lucide-react";
import { FormEvent, useState } from "react";
import { Navigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { RecoveryCodeNotice } from "../components/RecoveryCodeNotice";
import { ErrorScreen, LoadingScreen } from "../components/ScreenState";
import { api } from "../lib/api";
import { clearPreferredChildId } from "../lib/preferred-child";
import { queryClient } from "../lib/query";

export function AccountSecurityPage() {
  const data = useQuery({ queryKey: ["bootstrap"], queryFn: api.bootstrap });
  const [nickname, setNickname] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [showLogoutAll, setShowLogoutAll] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletePhrase, setDeletePhrase] = useState("");

  const updateProfile = useMutation({
    mutationFn: () => api.updateAccount({ nickname: nickname.trim() }),
    onSuccess: async () => {
      setNickname("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bootstrap"] }),
        queryClient.invalidateQueries({ queryKey: ["session"] }),
      ]);
    },
  });
  const changePassword = useMutation({
    mutationFn: () => {
      if (newPassword !== confirmPassword) throw new Error("两次输入的新密码不一致");
      return api.changePassword({ currentPassword, newPassword });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["session"], result);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setRecoveryCode(result.recoveryCode ?? "");
    },
  });
  const logoutAll = useMutation({
    mutationFn: api.logoutAll,
    onSuccess: exitToLogin,
  });
  const deleteAccount = useMutation({
    mutationFn: () => api.deleteAccount({ password: deletePassword }),
    onSuccess: exitToLogin,
  });

  function exitToLogin() {
    clearPreferredChildId();
    queryClient.clear();
    window.location.assign("/");
  }

  if (data.isPending) return <LoadingScreen />;
  if (data.isError) return <ErrorScreen message={data.error.message} onRetry={() => void data.refetch()} />;
  if (data.data.account.authType !== "account") return <Navigate to="/profile" replace />;
  if (recoveryCode) return <section className="screen account-security-page"><RecoveryCodeNotice code={recoveryCode} onContinue={() => setRecoveryCode("")} /></section>;

  function submitProfile(event: FormEvent) {
    event.preventDefault();
    updateProfile.mutate();
  }

  function submitPassword(event: FormEvent) {
    event.preventDefault();
    changePassword.mutate();
  }

  function submitDelete(event: FormEvent) {
    event.preventDefault();
    deleteAccount.mutate();
  }

  return (
    <section className="screen account-security-page">
      <PageHeader title="账号与安全" back />
      <div className="security-summary"><ShieldAlert /><div><strong>{data.data.account.phoneMasked}</strong><p>修改密码会同时退出其他设备</p></div></div>

      <section className="security-card">
        <h2><UserRound /> 修改家长称呼</h2>
        <form onSubmit={submitProfile}><label><span>新的称呼</span><input value={nickname} maxLength={40} placeholder={data.data.account.nickname} onChange={(event) => setNickname(event.target.value)} required /></label>{updateProfile.isError ? <p className="form-error">{updateProfile.error.message}</p> : null}<button className="small-button" disabled={updateProfile.isPending}>{updateProfile.isPending ? "保存中…" : "保存称呼"}</button></form>
      </section>

      <section className="security-card">
        <h2><KeyRound /> 修改密码</h2>
        <p>保存后会生成新的恢复码，并立即使其他设备的旧会话失效。</p>
        <form onSubmit={submitPassword}>
          <label><span>当前密码</span><input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required /></label>
          <label><span>新密码</span><input type="password" autoComplete="new-password" minLength={8} maxLength={64} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required /></label>
          <label><span>确认新密码</span><input type="password" autoComplete="new-password" minLength={8} maxLength={64} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required /></label>
          {changePassword.isError ? <p className="form-error">{changePassword.error.message}</p> : null}
          <button className="small-button" disabled={changePassword.isPending}>{changePassword.isPending ? "修改中…" : "修改密码"}</button>
        </form>
      </section>

      <section className="security-card">
        <h2><LogOut /> 退出所有设备</h2>
        <p>使当前账号在所有手机和浏览器上的会话立即失效。</p>
        {showLogoutAll ? <div className="inline-confirm"><span>确认退出全部设备？</span><button className="secondary-button" type="button" onClick={() => setShowLogoutAll(false)}>取消</button><button className="danger-button" type="button" disabled={logoutAll.isPending} onClick={() => logoutAll.mutate()}>{logoutAll.isPending ? "处理中…" : "确认退出"}</button></div> : <button className="secondary-button" type="button" onClick={() => setShowLogoutAll(true)}>退出所有设备</button>}
      </section>

      <section className="security-card danger-zone">
        <h2><Trash2 /> 注销账号</h2>
        <p>永久删除账号、全部孩子、作业、计划和完成记录，无法恢复。</p>
        <form onSubmit={submitDelete}>
          <label><span>当前密码</span><input type="password" autoComplete="current-password" value={deletePassword} onChange={(event) => setDeletePassword(event.target.value)} required /></label>
          <label><span>输入“注销账号”确认</span><input value={deletePhrase} onChange={(event) => setDeletePhrase(event.target.value)} required /></label>
          {deleteAccount.isError ? <p className="form-error">{deleteAccount.error.message}</p> : null}
          <button className="danger-button" disabled={deleteAccount.isPending || deletePhrase !== "注销账号"}>{deleteAccount.isPending ? "注销中…" : "永久注销账号"}</button>
        </form>
      </section>
    </section>
  );
}
